import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../UI/dialog';
import Button from '../UI/Button';
import { strategiesAPI } from '../../services/api';

const EVENTS = [
  ['order_submitted', 'Order Submitted', 'When an order is sent to the broker'],
  ['order_filled', 'Order Filled', 'When an order is filled'],
  ['order_rejected', 'Order Rejected', 'When an order is rejected'],
  ['order_canceled', 'Order Canceled', 'When an order is canceled'],
  ['position_opened', 'Position Opened', 'When a new position is created'],
  ['position_closed', 'Position Closed', 'When a position is fully closed'],
  ['stop_loss_triggered', 'Stop Loss Triggered', 'When a stop loss exit fires'],
  ['strategy_stopped', 'Strategy Stopped', 'When the strategy auto-stops'],
  ['strategy_error', 'Strategy Error', 'When a tick error occurs'],
];

/**
 * Notification preferences modal.
 *
 * Two modes:
 * 1. **Controlled** (builder): pass `prefs` + `onPrefsChange` — state is managed externally.
 * 2. **Standalone** (manager/floor): pass `strategyId` — loads and saves directly via API.
 */
export default function NotificationPrefsModal({ open, onClose, prefs, onPrefsChange, strategyId }) {
  const isControlled = typeof onPrefsChange === 'function';
  const [local, setLocal] = useState(null);
  const [saving, setSaving] = useState(false);

  // For standalone mode: load prefs from strategy on open
  useEffect(() => {
    if (!open) return;
    if (isControlled) {
      setLocal(prefs ? { ...prefs } : null);
    } else if (strategyId) {
      strategiesAPI.getById(strategyId).then((res) => {
        const s = res.data || res;
        const np = s.notification_preferences;
        setLocal(np && typeof np === 'object' ? { ...np } : null);
      }).catch(() => {
        setLocal(null);
      });
    }
  }, [open, strategyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setKey = (key, value) => {
    const next = { ...(local || {}) };
    if (value === undefined) delete next[key];
    else next[key] = value;
    setLocal(Object.keys(next).length > 0 ? next : null);
  };

  const handleReset = () => setLocal(null);

  const handleDone = async () => {
    if (isControlled) {
      onPrefsChange(local);
      onClose();
    } else if (strategyId) {
      setSaving(true);
      try {
        await strategiesAPI.update(strategyId, { notification_preferences: local });
        toast.success('Notification preferences saved');
        onClose();
      } catch (err) {
        toast.error(`Failed to save: ${err.message}`);
      } finally {
        setSaving(false);
      }
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Notification Overrides</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground/60 -mt-2 mb-3">
          Override global defaults for this strategy. "Global" inherits from your account settings.
        </p>
        <div className="space-y-1">
          {EVENTS.map(([key, label, desc]) => {
            const val = local?.[key];
            return (
              <div key={key} className="flex items-center justify-between py-2 px-2 rounded hover:bg-accent/5">
                <div>
                  <p className="text-sm text-foreground">{label}</p>
                  <p className="text-[10px] text-muted-foreground/50">{desc}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {[
                    { v: undefined, label: 'Global', cls: 'text-muted-foreground/60' },
                    { v: true, label: 'On', cls: 'text-green-400' },
                    { v: false, label: 'Off', cls: 'text-red-400' },
                  ].map((opt) => (
                    <button
                      key={String(opt.v)}
                      type="button"
                      onClick={() => setKey(key, opt.v)}
                      className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                        val === opt.v || (val === undefined && opt.v === undefined)
                          ? `${opt.cls} bg-accent/20 font-semibold`
                          : 'text-muted-foreground/40 hover:text-muted-foreground hover:bg-accent/10'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={handleReset}>
            Reset to Global
          </Button>
          <Button onClick={handleDone} loading={saving}>
            {isControlled ? 'Done' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
